/**
 * useHomepageCards
 *
 * Signal-driven card ordering for the homepage.
 * Computes which cards to show and in what order based on
 * the user's journey state, data signals, and context.
 */

import { useMemo } from "react";
import type { HomepageGender, HomepageGoal } from "../../homepage.types";
import type {
  HomepageCard,
  JourneyDefinition,
  Pathway,
  PlanStep,
  TreatmentModality,
  JourneyViewerRole,
} from "../plan.types";
import { getJourneyForState } from "../journeys";
import { usePlanProgress, type StepProgress } from "./usePlanProgress";

export type UseHomepageCardsResult = {
  journey: JourneyDefinition;
  cards: HomepageCard[];
  progress: StepProgress[];
  activeStepIndex: number;
  activeStep: PlanStep | null;
};

/**
 * Find the first in-progress step
 */
function findActiveStepIndex(progress: StepProgress[]): number {
  const idx = progress.findIndex((p) => p.status === "in-progress");
  return idx >= 0 ? idx : 0;
}

/**
 * Build the card list based on current state
 */
function buildCards(
  journey: JourneyDefinition,
  progress: StepProgress[],
  activeIndex: number,
): HomepageCard[] {
  const cards: HomepageCard[] = [];
  const activeStep = journey.steps[activeIndex];
  const activeProgress = progress[activeIndex];

  // 1. Always show the plan stepper (bird's eye view)
  cards.push({
    type: "plan-stepper",
    priority: 100,
  });

  // 2. Show the active step card
  if (activeStep) {
    cards.push({
      type: "active-step",
      priority: 90,
      stepId: activeStep.id,
    });
  }

  // 3. Check for awaiting-results substeps
  if (activeStep?.substeps) {
    const awaitingSubsteps = activeStep.substeps.filter(
      (s) => s.status === "done-awaiting-results",
    );
    for (const sub of awaitingSubsteps) {
      cards.push({
        type: "awaiting-results",
        priority: 85,
        stepId: activeStep.id,
        substepId: sub.id,
        data: sub,
      });

      // Add "in the meantime" cards for actionable items while waiting
      if (sub.inTheMeantime && sub.inTheMeantime.length > 0) {
        cards.push({
          type: "in-the-meantime",
          priority: 80,
          stepId: activeStep.id,
          substepId: sub.id,
          data: sub.inTheMeantime,
        });
      }
    }

    // Check for results-in substeps (new results to review)
    const resultsIn = activeStep.substeps.filter(
      (s) => s.status === "results-in",
    );
    for (const sub of resultsIn) {
      cards.push({
        type: "awaiting-results",
        priority: 95, // Higher priority — new data to review
        stepId: activeStep.id,
        substepId: sub.id,
        data: sub,
      });
    }
  }

  // 4. Optimization window (e.g., male sperm optimization)
  if (activeStep?.optimizationWindow) {
    cards.push({
      type: "optimization-window",
      priority: 75,
      stepId: activeStep.id,
      data: activeStep.optimizationWindow,
    });
  }

  // 5. Condition-specific insights
  if (activeStep?.conditionModifiers) {
    for (const mod of activeStep.conditionModifiers) {
      cards.push({
        type: "condition-insight",
        priority: 70,
        stepId: activeStep.id,
        data: mod,
      });
    }
  }

  // 6. Emotional context (phase-appropriate)
  const emotionalPhase = deriveEmotionalPhase(activeStep, activeProgress);
  if (emotionalPhase) {
    cards.push({
      type: "emotional-context",
      priority: 30,
      data: emotionalPhase,
    });
  }

  // Sort by priority (highest first)
  cards.sort((a, b) => b.priority - a.priority);

  return cards;
}

/**
 * Derive emotional phase from current step context
 */
function deriveEmotionalPhase(
  step: PlanStep | undefined,
  progress: StepProgress | undefined,
) {
  if (!step) return null;

  switch (step.id) {
    case "learn":
      return {
        phase: "overwhelmed-start" as const,
        normalizedMessage:
          "It's completely normal to feel overwhelmed at the start. You don't need to know everything right now — just the next step.",
        communityDataPoint: "78% of users felt the same way when they started",
      };
    case "test":
      return {
        phase: "testing-anxiety" as const,
        normalizedMessage:
          "Waiting for test results can feel heavy. Remember: these numbers are data points, not verdicts. They help your doctor plan the best approach for you.",
        communityDataPoint:
          "Most users complete all baseline testing within 2-3 weeks",
      };
    case "wait":
      return {
        phase: "tww-hope-fear" as const,
        normalizedMessage:
          "The two-week wait is one of the hardest parts. Every feeling you're having is valid — hope, fear, obsession over symptoms. You're not alone in this.",
        communityDataPoint:
          "You're one of thousands going through this right now",
      };
    default:
      return null;
  }
}

/**
 * Main hook: computes the homepage card stack
 */
export function useHomepageCards(
  gender: HomepageGender | null,
  goal: HomepageGoal | null,
  pathway: Pathway,
  modality?: TreatmentModality | null,
  viewerRole?: JourneyViewerRole | null,
): UseHomepageCardsResult {
  const journey = useMemo(
    () => getJourneyForState(gender, goal, pathway, modality, viewerRole),
    [gender, goal, pathway, modality, viewerRole],
  );

  const { progress } = usePlanProgress(journey.steps, journey.key);

  const activeStepIndex = useMemo(
    () => findActiveStepIndex(progress),
    [progress],
  );

  const activeStep = journey.steps[activeStepIndex] ?? null;

  const cards = useMemo(
    () => buildCards(journey, progress, activeStepIndex),
    [journey, progress, activeStepIndex],
  );

  return {
    journey,
    cards,
    progress,
    activeStepIndex,
    activeStep,
  };
}
